// Programmatic content generator for the service × city combo pages.
// Every combination of the 14 services and 40 Manitoba cities gets a routable
// page under /services/[serviceSlug]/[citySlug]. Content is generated with
// enough structural + phrasing variation that each page reads unique while
// still targeting the service+city long-tail keyword.

import { services, serviceBySlug } from "./services";
import { cities, cityBySlug } from "./cities";

// ---------- shape ----------
// Every combo yields:
//   { slug, service, city, title, description, keywords, sections: [{h,p}...] }

// Small deterministic hash so different combos pick different phrasing variants.
function hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}
const pick = (arr, seed) => arr[seed % arr.length];

// Descriptors keyed to region — used for city-context paragraph.
const REGION_DESCRIPTOR = {
  "Capital Region":
    "the operational spine of the province — a diversified urban economy carrying finance, logistics, healthcare, aerospace, and a growing technology sector",
  "Westman":
    "Manitoba's western agricultural and light-industrial belt, anchored by Brandon and radiating outward through Neepawa, Minnedosa, Virden, and Souris",
  "Southeast":
    "one of the fastest-growing rural service regions in Canada, with a manufacturing and cross-border-trade base that is scaling steadily year over year",
  "Pembina Valley":
    "a manufacturing and agri-processing corridor with strong export orientation and steady residential growth",
  "Interlake":
    "the Lake Winnipeg service region — a mix of year-round commercial activity and a substantial seasonal footprint on the south basin",
  "Central Plains":
    "a food-processing and agri-industrial heartland along the Yellowhead corridor",
  "Red River":
    "the Highway 75 corridor south of Winnipeg — a mix of agricultural service centres and cross-border commercial traffic",
  "Eastman":
    "the Whiteshell approach and Winnipeg River corridor — a blend of industrial, hydroelectric, and cottage-country footprints",
  "Parkland":
    "the northwestern service region between Riding Mountain National Park and the Saskatchewan border — a forestry, agri-service, and community-services economy",
  "Northern":
    "the resource-sector heartland of the province — nickel, copper, zinc, gold, forestry, rail, hydroelectric, and Arctic port activity",
};

// Service-specific site archetype used in "typical sites" paragraph.
const SITE_ARCHETYPES = {
  "mining-security": [
    "mine gates, mill perimeters, fuel depots, contractor lay-down yards, warehouse loading bays, and administrative approach roads",
    "concentrator and smelter footprints, magazine access approaches, tank farms, ore-storage areas, and shift-change transfer points",
  ],
  "mobile-patrol": [
    "industrial parks, commercial plazas, dealer lots, warehouse yards, and construction perimeters",
    "big-box retail centres, professional buildings, auto dealerships, self-storage facilities, and business improvement zones",
  ],
  "event-security": [
    "convention centres, arenas, festival grounds, theatres, banquet halls, and community event venues",
    "concert venues, corporate gala spaces, agricultural fair grounds, sports facilities, and licensed hospitality footprints",
  ],
  "corporate-security": [
    "office towers, executive floors, corporate campuses, professional building lobbies, and reception areas",
    "downtown office blocks, financial-district lobbies, professional service firm reception areas, and executive-floor gatekeeping",
  ],
  "construction-site-security": [
    "active construction perimeters, laydown yards, material storage compounds, temporary site offices, and equipment staging areas",
    "residential subdivision developments, commercial infill projects, institutional builds, industrial expansion projects, and infrastructure works",
  ],
  "retail-loss-prevention": [
    "shopping centres, big-box footprints, department stores, standalone retail chains, and specialty independent stores",
    "grocery stores, apparel retailers, electronics retailers, mass-market department stores, and mall corridors",
  ],
  "residential-security": [
    "condominium towers, high-rise residential complexes, gated communities, single-home enclaves, and higher-density residential blocks",
    "multi-tower developments, luxury single-home neighbourhoods, mid-rise residential buildings, and community-managed private streets",
  ],
  "executive-protection": [
    "corporate executive movement, principal transport, venue arrivals, sensitive termination protocols, and family-office details",
    "boardroom-to-boardroom transitions, hotel-to-venue movement, private-aviation transfers, and public-appearance choreography",
  ],
  "concierge-security": [
    "hotel front desks, condominium concierge stations, hospitality-forward lobbies, and mixed-use residential entries",
    "boutique hotel receptions, high-rise condominium lobbies, mixed-use tower ground floors, and private club front doors",
  ],
  "alarm-response": [
    "commercial alarm points, retail after-hours activations, industrial-facility alarms, and residential monitored premises",
    "monitored bank branch alarms, jewellery-store activations, warehouse motion triggers, and remote industrial site alarms",
  ],
  "warehouse-industrial-security": [
    "distribution centres, fulfillment warehouses, cold-storage facilities, freight yards, and cargo-handling docks",
    "TAPA-influenced logistics operations, food-grade warehousing, hazardous-goods storage, and cross-dock operations",
  ],
  "oil-gas-site-security": [
    "wellheads, pump stations, tank batteries, gathering facilities, pipeline right-of-ways, and workover-rig positions",
    "flow-station approaches, produced-water storage sites, compressor stations, and drilling-rig staging areas",
  ],
  "healthcare-security": [
    "hospital emergency departments, ambulatory clinics, long-term care facilities, mental-health units, and outpatient buildings",
    "specialty clinics, primary-care facilities, dental offices, community-health centres, and campus hospital footprints",
  ],
  "education-campus-security": [
    "K-12 school perimeters, private college campuses, post-secondary buildings, event-hosting halls, and library approaches",
    "residence-adjacent zones, school event footprints, campus common areas, and after-hours facility use",
  ],
  "fire-watch": [
    "construction sites during hot-work operations, warehouses under sprinkler-system service, retail footprints during alarm upgrades, and hospitality venues during suppression-system testing",
    "post-incident restoration premises, healthcare buildings during suppression maintenance, condominium interim-protection windows, and industrial facilities during scheduled fire-system service",
  ],
};

// Response-protocol paragraph variants (varied so combos read distinctly).
const RESPONSE_VARIANTS = [
  "Every deployment is documented with GPS-verified checkpoint scans, time-stamped shift reports, and photograph-supported incident logs delivered directly to the client's operations lead within the operating cycle. Where the engagement involves alarm response, we operate on tight SLA windows and escalate to the Winnipeg Police Service or the local RCMP detachment when a criminal event is confirmed. Documentation is precise; escalation is briefed; and the operational envelope stays intact through every shift.",
  "Officers scan NFC checkpoints across the site's critical zones, produce time-stamped patrol reports with embedded photographs, and route escalation decisions through a senior supervisor before dispatch. Every anomaly is captured; every response is logged; every daily summary reaches the client on schedule. Where an event rises above private-security jurisdiction, the handoff to the appropriate RCMP detachment is clean, documented, and preserves chain-of-custody discipline from first contact through statement submission.",
  "Documentation is the deliverable. We produce audit-ready shift ledgers, incident narratives with photograph evidence, and monthly trend summaries that surface the patterns behind isolated events. Where an event requires law-enforcement handoff, we hold chain-of-custody discipline from first contact through statement submission. Reports support insurance filings, WCB requirements, internal loss reviews, and downstream operational planning without additional interpretation.",
];

const CLOSER_VARIANTS = [
  "Manitoba Security Services exists to serve Manitoba. Every officer we deploy, every report we produce, and every escalation we handle operates under a single operating standard — the same standard applied whether the site is a downtown Winnipeg lobby or a fly-in mining camp on the northern edge of the province. We do not chase national contracts. We do not accept engagements we cannot staff to the standard. We do not compromise the operating envelope for margin.",
  "One operating standard, executed province-wide. That is the sentence at the top of our operating charter, and it dictates every training decision, every deployment decision, and every reporting decision we make. Our commitment to the community begins the moment the shift begins — and it does not end when the shift ends, because the documentation, the audit trail, and the post-shift review continue past the handover.",
  "Selection over recruitment. Documentation over improvisation. Discipline over drama. Every engagement we accept is delivered against those three principles — no exceptions, no shortcuts, no compromises. The officers on your site are the officers we chose specifically for your site, and the reports you receive are the reports we produced under the standard we hold ourselves to across every deployment in the province.",
];

const COVERAGE_VARIANTS = [
  "scales from event-driven single-shift coverage through to 24/7 permanent posture. Where a client operates on a fixed roster — for example an overnight-only requirement, a weekend-only requirement, or a shift-change coverage window — we build the schedule accordingly. Where a client requires full continuous coverage, we deliver it with cross-trained officers whose profiles are matched to the environment presents. Officer rotation is documented, supervision cadence is enforced, and continuity of context is preserved from one shift to the next.",
  "operates on rosters calibrated to the client's operational envelope. High-hazard windows attract additional supervision; low-cadence windows are staffed for cost efficiency without giving up presence quality. We hold reserve capacity for unplanned coverage — sudden absences, escalating incidents, event-day expansions — so the client is never exposed to a staffing gap. Every roster is reviewed monthly against operational patterns and adjusted where the data supports change.",
  "runs on a documented schedule reviewed at agreed intervals with the client's operations lead. Officer assignments are stable so the client sees the same faces across weeks; where rotation is necessary, incoming officers are briefed against a written site file before shift start. Supervision passes cover every posting on every shift; escalations reach a senior on call within minutes; and continuity of context is preserved from month to month even when specific personnel change.",
];

// ---------- generator ----------
export function buildCombo(service, city) {
  if (!service || !city) return null;

  const seed = hash(service.slug + "|" + city.slug);
  const regionText =
    REGION_DESCRIPTOR[city.region] || "the surrounding Manitoba region";
  const sites =
    (SITE_ARCHETYPES[service.slug] && pick(SITE_ARCHETYPES[service.slug], seed)) ||
    "commercial, industrial, and community sites";
  const responseText = pick(RESPONSE_VARIANTS, seed);
  const closerText = pick(CLOSER_VARIANTS, seed);

  const title = `${service.name} in ${city.name}, Manitoba — Manitoba Security Services`;

  const description = `${service.name} coverage in ${city.name}, ${city.region}. Manitoba Security Services delivers ${service.tag.toLowerCase()} — auditable, documented, executed province-wide.`;

  const keywords = Array.from(new Set([
    `${service.name} ${city.name}`,
    `${service.name.toLowerCase()} ${city.name}`,
    `security guard services ${city.name}`,
    `${city.name} ${service.name.toLowerCase()}`,
    `${service.name} Manitoba`,
    `${city.name} security guards`,
    `${city.name} Manitoba security company`,
    ...(service.keywords || []).slice(0, 4),
    ...(city.keywords || []).slice(0, 3),
  ]));

  const sections = [
    {
      h: `${service.name} in ${city.name}`,
      p: `Manitoba Security Services delivers ${service.name.toLowerCase()} across ${city.name} and the surrounding ${city.region} coverage area. ${service.hero} Our ${city.name} deployments are staffed and dispatched under one operating standard — the same standard applied whether the assignment is a downtown lobby, a mine gate, or an active construction perimeter.`,
    },
    {
      h: `The ${city.name} context`,
      p: `${city.name} sits inside ${regionText}. That geographic and industrial context shapes every ${service.name.toLowerCase()} program we build here — from the sites we typically protect to the reporting cadence we hold, from the community relationships we maintain to the way we coordinate with local law-enforcement. Our officers are drawn to fit the tone and pace of ${city.name} specifically, not rotated in from unrelated environments.`,
    },
    {
      h: `Typical ${service.name.toLowerCase()} sites in ${city.name}`,
      p: `Across ${city.name}, our ${service.name.toLowerCase()} coverage protects ${sites}. Each engagement is scoped against the client's specific risk profile — the hours of exposure, the value at rest, the pattern of traffic through the site, and the escalation ladder that governs any incident response. We do not deliver a generic deployment; we deliver a program calibrated to the operational envelope of the specific ${city.name} asset.`,
    },
    {
      h: `Response protocol`,
      p: responseText,
    },
    {
      h: `Coverage cadence`,
      p: `${service.name} in ${city.name} ${pick(COVERAGE_VARIANTS, seed + 7)}`,
    },
    {
      h: `The Manitoba Security standard`,
      p: closerText,
    },
  ];

  return {
    slug: `${service.slug}/${city.slug}`,
    serviceSlug: service.slug,
    citySlug: city.slug,
    service,
    city,
    title,
    description,
    keywords,
    sections,
  };
}

// Pre-compute every combo. 14 * 40 = 560 pages.
export const combos = services.flatMap((s) =>
  cities.map((c) => buildCombo(s, c))
);

export function comboByPair(serviceSlug, citySlug) {
  const s = serviceBySlug(serviceSlug);
  const c = cityBySlug(citySlug);
  return buildCombo(s, c);
}

// Utility used by prerender to enumerate slugs.
export const comboSlugs = combos.map((c) => ({
  serviceSlug: c.serviceSlug,
  citySlug: c.citySlug,
}));
